import { useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { Button, styles, useModalControl } from '@frontend/design-system';
import { ContactDetails } from '../../components/onboarding-flow-components/constants';
import { StackedCardWizard } from '../../components/onboarding-flow-components/stacked-card-wizard';
import { useOnboardingFlowInfo } from '../../hooks/onboarding-flow-hooks/use-onboarding-flow-info';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { AppointmentTypeStep } from './appointment-type-step';
import { BookingStep } from './booking-step';
import { LocationStep } from './location-step';
import { PracticeVerticalStep } from './practice-vertical-step';
import { ProviderStep } from './provider-step';
import { OnboardingStep, OnboardingWizardData } from './types';

export const OnboardingWizard = () => {
  const { t } = useTranslation('schedule-onboarding');
  const [contactDetails, setContactDetails] = useState<ContactDetails | undefined>();
  const { closeSettings } = useSettingsNavigate();
  const [appointmentType, setAppointmentType] = useState<{ name: string; duration: number }>({ name: '', duration: 0 });
  const { modalProps, triggerProps } = useModalControl();
  const { onboardingFlowInfo, setOnboardingFlowInfo } = useOnboardingFlowInfo();
  const { selectedLocationIds } = useSchedulingLocationInfo();
  const showLocationStep = selectedLocationIds.length > 1;

  const handleOnComplete = (data: OnboardingWizardData) => {
    const providerStepData = data[OnboardingStep.Provider];
    if (providerStepData?.contactDetails) {
      setContactDetails(providerStepData.contactDetails);
    }
    const apptTypeInfo = data[OnboardingStep.AppointmentType];
    setAppointmentType({
      name: apptTypeInfo?.appointmentName || '',
      duration: (apptTypeInfo?.duration === 'Custom' ? apptTypeInfo.customDuration : apptTypeInfo?.duration) || 60,
    });
    triggerProps.onClick();
  };

  const handleSecondaryAction = () => {
    if (onboardingFlowInfo.shouldSetSkipWizardStatus) {
      setOnboardingFlowInfo({ status: 'skipped' });
    } else {
      setOnboardingFlowInfo({ shouldSetSkipWizardStatus: true });
    }
    closeSettings();
  };

  if (modalProps.show) {
    return (
      <BookingStep
        {...modalProps}
        contactDetails={contactDetails}
        appointmentTypeName={appointmentType.name}
        appointmentTypeDuration={appointmentType.duration}
      />
    );
  }

  return (
    <StackedCardWizard
      onComplete={handleOnComplete}
      secondaryAction={
        <div css={styles.flexCenter}>
          <Button variant='tertiary' css={buttonStyle} onClick={handleSecondaryAction}>
            {t("I don't want help setting up")}
          </Button>
        </div>
      }
    >
      <PracticeVerticalStep />
      {showLocationStep && <LocationStep />}
      <AppointmentTypeStep />
      <ProviderStep />
    </StackedCardWizard>
  );
};

const buttonStyle = css({
  textTransform: 'none',
  '&:hover, &:active': {
    backgroundColor: 'transparent !important',
  },
});
